/**
 * JSON-LD builders for GeoVera structured data (Organization, SoftwareApplication, WebSite, WebPage).
 */
import { absoluteUrl, SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE, type PageSeo } from './seoConfig';
import {
  CLIPTOCAD_DESCRIPTION,
  CLIPTOCAD_FAQS,
  CLIPTOCAD_VERSION,
} from '../components/cliptocad/cliptocadCopy';

export function organizationJsonLd() {
  return {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/favicon.svg`,
    },
    description:
      'GeoVera provides WebGIS, DIGIPIN geo-grid tools, QGIS plugins, and digital property cards for spatial and survey workflows.',
    foundingDate: '2024',
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    sameAs: [] as string[],
  };
}

export function websiteJsonLd() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    description:
      'AI-powered WebGIS platform with DIGIPIN encoding, QGIS plugins, and shareable digital property maps.',
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-IN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/login?redirect=%2Fdashboard`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function softwareApplicationsJsonLd() {
  return [
    {
      '@type': 'SoftwareApplication',
      '@id': `${SITE_URL}/#webgis`,
      name: 'GeoVera WebGIS',
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Geographic Information System',
      operatingSystem: 'Web',
      url: `${SITE_URL}/`,
      description:
        'Browser-based WebGIS for vector/raster layers, measurement, drawing, spatial analysis, and digital property cards.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
      },
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${SITE_URL}/digipin-encoder#app`,
      name: 'DIGIPIN Encoder Pro',
      applicationCategory: 'DesktopApplication',
      operatingSystem: 'Windows, macOS, Linux (QGIS 3.16+)',
      url: `${SITE_URL}/digipin-encoder`,
      description:
        'QGIS plugin for India Post DIGIPIN encode/decode and batch layer attributes at ~4m grid resolution.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
      },
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${SITE_URL}/cliptocad#app`,
      name: 'ClipToCAD',
      applicationCategory: 'DesktopApplication',
      softwareVersion: CLIPTOCAD_VERSION,
      operatingSystem: 'Windows, macOS, Linux (QGIS 3.16–4.x)',
      url: `${SITE_URL}/cliptocad`,
      description: CLIPTOCAD_DESCRIPTION,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
      },
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ];
}

export function webPageJsonLd(page: PageSeo) {
  const url = absoluteUrl(page.path);
  return {
    '@type': page.schemaType === 'CollectionPage' ? 'CollectionPage' : 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: page.title,
    description: page.description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-IN',
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: page.ogImage || DEFAULT_OG_IMAGE,
    },
  };
}

export function faqPageJsonLd(page: PageSeo, faqs: readonly { q: string; a: string }[]) {
  return {
    '@type': 'FAQPage',
    '@id': `${absoluteUrl(page.path)}#faq`,
    url: absoluteUrl(page.path),
    name: page.title,
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };
}

export function breadcrumbJsonLd(page: PageSeo) {
  const items: { name: string; path: string }[] = [{ name: 'Home', path: '/' }];
  if (page.path === '/') {
    return null;
  }
  if (
    page.path.startsWith('/qgis-plugins') ||
    page.path.includes('digipin') ||
    page.path.includes('cliptocad')
  ) {
    if (page.path !== '/qgis-plugins') {
      items.push({ name: 'QGIS Plugins', path: '/qgis-plugins' });
    }
  }
  items.push({ name: page.title.split('|')[0].trim(), path: page.path });

  return {
    '@type': 'BreadcrumbList',
    '@id': `${absoluteUrl(page.path)}#breadcrumb`,
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

/** Full @graph for a page (homepage includes SoftwareApplication catalog) */
export function buildJsonLdGraph(page: PageSeo): Record<string, unknown> {
  const graph: unknown[] = [organizationJsonLd(), websiteJsonLd(), webPageJsonLd(page)];

  const crumbs = breadcrumbJsonLd(page);
  if (crumbs) graph.push(crumbs);

  if (page.path === '/') {
    graph.push(...softwareApplicationsJsonLd());
  } else if (page.path === '/digipin-encoder') {
    graph.push(softwareApplicationsJsonLd()[1]);
  } else if (page.path === '/cliptocad') {
    graph.push(softwareApplicationsJsonLd()[2]);
  } else if (page.path === '/cliptocad/issues') {
    graph.push(faqPageJsonLd(page, CLIPTOCAD_FAQS));
  } else if (page.path === '/qgis-plugins') {
    graph.push(...softwareApplicationsJsonLd().slice(1));
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
